import SelectInput from '@oberoninternal/travelbase-ds/components/form/SelectInput';
import { TextInputField } from '@oberoninternal/travelbase-ds/components/form/TextInput';
import Sidebar from '@oberoninternal/travelbase-ds/components/layout/Sidebar';
import { Label } from '@oberoninternal/travelbase-ds/components/primitive/Label';
import { Sesame } from '@oberoninternal/travelbase-ds/hooks/useSesame';
import React, { FC } from 'react';
import { IntlShape, useIntl, FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import countryOptions from '../../../constants/countryOptions';
import { PartnerParams } from '../../../entities/PartnerParams';
import {
    usePartnerDataQuery,
    useCreateVisitorRegistrationMutation,
    useEditVisitorRegistrationMutation,
    AllVisitorRegistrationsDocument,
    IdentificationTypeEnum,
    Maybe,
} from '../../../generated/graphql';
import NumberInput from '../../atoms/NumberInput';
import { SidebarSeperator } from '../../atoms/Seperator';
import SidebarField from '../../atoms/SidebarField';
import DayPickerInputField from '../../molecules/DayPickerInputField';
import FormScreen from '../../organisms/FormScreen';
import { identificationTypeTranslations, VisitorNode } from './VisitorRegisterHistoryList';
import * as Yup from 'yup';
import styled from 'styled-components';

type FormValues = {
    firstName: string;
    lastName: string;
    city: string;
    countryCode: string;
    totalPersons: number;
    identificationType?: string;
    arrivalDate: Date;
    departureDate: Date;
    rentalUnitId?: string;
};

function isSameDate(date1: Date, date2: Date) {
    return date1.getTime() === date2.getTime();
}

const validationSchema = (intl: IntlShape) =>
    Yup.object().shape({
        identificationType: Yup.string(),
        firstName: Yup.string().required(intl.formatMessage({ defaultMessage: 'Voornaam is verplicht' })),
        lastName: Yup.string().required(intl.formatMessage({ defaultMessage: 'Achternaam is verplicht' })),
        city: Yup.string().required(intl.formatMessage({ defaultMessage: 'Woonplaats is verplicht' })),
        countryCode: Yup.string().required(intl.formatMessage({ defaultMessage: 'Land is verplicht' })),
        totalPersons: Yup.number().required(intl.formatMessage({ defaultMessage: 'Aantal personen is verplicht' })),
        arrivalDate: Yup.date().required(intl.formatMessage({ defaultMessage: 'Aankomstdatum is verplicht' })),
        departureDate: Yup.date()
            .required(intl.formatMessage({ defaultMessage: 'Vertrekdatum is verplicht' }))
            .min(Yup.ref('arrivalDate'), 'Vertrekdatum moet na aankomstdatum zijn')
            .test({
                name: 'same',
                exclusive: false,
                params: {},
                message: 'Vertrekdatum moet na aankomstdatum zijn',
                test(value) {
                    const startDate = new Date(this.parent.arrivalDate);
                    const endDate = new Date(value);
                    return !isSameDate(startDate, endDate);
                },
            }),
    });

const VisitorRegistrationSidebar: FC<{
    sesame: Sesame;
    activeRegistration?: Maybe<VisitorNode>;
    setActiveRegistrationId: (id: string | undefined) => void;
}> = ({ activeRegistration, sesame, setActiveRegistrationId }) => {
    const { partnerId } = useParams<PartnerParams>();
    const { data } = usePartnerDataQuery({ variables: { partnerId } });
    const accommodations = data?.partner?.accommodations ?? [];
    const rentalUnits = accommodations.flatMap(accomodation => accomodation?.rentalUnits ?? []);

    const accomodationOptions = rentalUnits.map(rentalUnit => ({
        label: `${rentalUnit.name} (${rentalUnit.code})`,
        value: rentalUnit.id,
    }));
    const intl = useIntl();
    const { formatMessage } = intl;
    const refetchConfig = {
        refetchQueries: [
            {
                query: AllVisitorRegistrationsDocument,
                variables: {
                    partnerId,
                    first: 40,
                    rentalUnitIds: [],
                    startDate: null,
                    endDate: null,
                },
            },
        ],
    };
    const [mutate] = useCreateVisitorRegistrationMutation(refetchConfig);
    const [editMutate] = useEditVisitorRegistrationMutation(refetchConfig);
    const isEditing = !!activeRegistration?.node?.id;

    const getInitialValues = () => ({
        firstName: activeRegistration?.node?.firstName ?? '',
        lastName: activeRegistration?.node?.lastName ?? '',
        city: activeRegistration?.node?.city ?? '',
        countryCode: activeRegistration?.node?.countryCode ?? 'NL',
        totalPersons: activeRegistration?.node?.totalPersons ?? 1,
        arrivalDate: activeRegistration?.node?.arrivalDate ? new Date(activeRegistration.node.arrivalDate) : new Date(),
        departureDate: activeRegistration?.node?.departureDate
            ? new Date(activeRegistration.node.departureDate)
            : new Date(),
        identificationType: activeRegistration?.node?.identificationType ?? '',
    });

    return (
        <Sidebar {...sesame}>
            <FormScreen<FormValues>
                validationSchema={validationSchema(intl)}
                handleSubmit={async values => {
                    if (isEditing) {
                        await editMutate({
                            variables: {
                                visitorRegistrationId: activeRegistration.node!.id,
                                identificationType: values?.identificationType
                                    ? (values?.identificationType as IdentificationTypeEnum)
                                    : undefined,
                            },
                        });
                    } else {
                        await mutate({
                            variables: {
                                firstName: values.firstName,
                                lastName: values.lastName,
                                city: values.city,
                                countryCode: values.countryCode,
                                totalPersons: values.totalPersons,
                                identificationType: values?.identificationType
                                    ? (values?.identificationType as IdentificationTypeEnum)
                                    : undefined,
                                arrivalDate: values.arrivalDate,
                                departureDate: values.departureDate,
                                rentalUnitId: values.rentalUnitId ?? '',
                            },
                        });
                    }
                    sesame.onClose();
                    setActiveRegistrationId(undefined);
                }}
                variant="sidebar"
                initialValues={getInitialValues()}
            >
                {() => (
                    <>
                        {activeRegistration?.node?.id === undefined && (
                            <>
                                <SidebarField variant="small">
                                    <Label htmlFor="rentalUnitId">
                                        <FormattedMessage defaultMessage="Accommodatie" />
                                    </Label>
                                    <SelectInput
                                        name="rentalUnitId"
                                        isSearchable
                                        placeholder={formatMessage({ defaultMessage: 'Selecteer accomodatie' })}
                                        options={accomodationOptions}
                                    />
                                </SidebarField>
                                <SidebarSeperator />
                                <OverlayDatePicker>
                                    <SidebarField label={formatMessage({ defaultMessage: 'Startdatum' })}>
                                        <DayPickerInputField enabledPast disabledFuture name="arrivalDate" />
                                    </SidebarField>
                                </OverlayDatePicker>
                                <SidebarField label={formatMessage({ defaultMessage: 'Einddatum' })}>
                                    <DayPickerInputField enabledPast name="departureDate" />
                                </SidebarField>
                                <SidebarSeperator />
                            </>
                        )}

                        <SidebarField variant="small">
                            <Label htmlFor="identificationType">
                                <FormattedMessage defaultMessage="Type identiteitsbewijs" />
                            </Label>
                            <SelectInput
                                placeholder={formatMessage({ defaultMessage: 'Selecteer' })}
                                id="identificationType"
                                options={[
                                    {
                                        label: identificationTypeTranslations[IdentificationTypeEnum.Passport],
                                        value: IdentificationTypeEnum.Passport,
                                    },
                                    {
                                        label: identificationTypeTranslations[IdentificationTypeEnum.DrivingLicense],
                                        value: IdentificationTypeEnum.DrivingLicense,
                                    },
                                    {
                                        label: identificationTypeTranslations[IdentificationTypeEnum.Card],
                                        value: IdentificationTypeEnum.Card,
                                    },
                                ]}
                                name="identificationType"
                            />
                        </SidebarField>
                        <>
                            <SidebarField variant="small">
                                <Label htmlFor="firstName">
                                    <FormattedMessage defaultMessage="Voornaam gast" />
                                </Label>
                                <TextInputField name="firstName" disabled={isEditing} />
                            </SidebarField>
                            <SidebarField variant="small">
                                <Label htmlFor="lastName">
                                    <FormattedMessage defaultMessage="Achternaam gast" />
                                </Label>
                                <TextInputField name="lastName" disabled={isEditing} />
                            </SidebarField>
                            <SidebarField variant="small">
                                <Label htmlFor="totalPersons">
                                    <FormattedMessage defaultMessage="Aantal personen" />
                                </Label>
                                <NumberInput name="totalPersons" disabled={isEditing} />
                            </SidebarField>
                            <SidebarField variant="small">
                                <Label htmlFor="city">
                                    <FormattedMessage defaultMessage="Woonplaats gast" />
                                </Label>
                                <TextInputField name="city" disabled={isEditing} />
                            </SidebarField>

                            <SidebarField variant="small">
                                <Label htmlFor="countryCode">
                                    <FormattedMessage defaultMessage="Land gast" />
                                </Label>
                                <SelectInput
                                    id="countryCode"
                                    name="countryCode"
                                    isSearchable
                                    placeholder={formatMessage({ defaultMessage: 'Selecteer land' })}
                                    options={countryOptions}
                                    disabled={isEditing}
                                />
                            </SidebarField>
                        </>
                    </>
                )}
            </FormScreen>
        </Sidebar>
    );
};

export default VisitorRegistrationSidebar;

const OverlayDatePicker = styled.div`
    z-index: ${({ theme }) => theme.zIndices.docked};
    position: relative;
`;
